import React, { useState, useEffect, useCallback } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { Timestamp } from 'firebase/firestore';

interface ASINReviewHistoryDashboardProps {
  product: PMT2Product | null;
}

interface ReviewEntry {
  id: string;
  date: Timestamp; // Firestore Timestamp (used as fallback)
  Date: string; // String date like MM/DD/YY or MM/DD/YYYY
  'Your Name': string;
  note: string;
  [key: string]: any;
}

/**
 * ASIN Review History Dashboard Component
 *
 * Displays historical ASIN review entries for a selected product
 * Each entry shows Days Ago, Date, Reviewer, and Notes as bullet points
 */
const ASINReviewHistoryDashboard: React.FC<ASINReviewHistoryDashboardProps> = ({ product }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewEntries, setReviewEntries] = useState<ReviewEntry[]>([]);

  // Memoize the fetch function to avoid recreating it on every render
  const fetchReviewHistory = useCallback(async (companyName: string, asin: string) => {
    // Reset state
    setReviewEntries([]);
    setError(null);
    setLoading(true);

    try {
      const entries = await pmt2Service.getASINReviewHistory(companyName, asin);

      // Only update state if the product hasn't changed during the fetch
      // Check product existence before accessing properties
      if (product && product.id === asin && product.companyName === companyName) {
         // Ensure entries have a valid date structure for sorting before setting state
         const validatedEntries = entries.map(e => ({
             ...e,
             // Ensure date fields exist, provide defaults if necessary for robust sorting
             Date: e.Date || '', // Default to empty string if missing
             date: e.date || Timestamp.fromDate(new Date(0)) // Default to epoch if missing
         }));
        setReviewEntries(validatedEntries);
      }
    } catch (err) {
      setError('Failed to load review history. Please try again later.');
    } finally {
      // Ensure loading is set to false even if product changed mid-fetch
      setLoading(false);
    }
  }, [product]); // Depend on product object

  // Effect to fetch data when product changes
  useEffect(() => {
    // Reset state when product changes
    setReviewEntries([]);
    setError(null);

    if (product) {
      fetchReviewHistory(product.companyName, product.id);
    } else {
        // If product becomes null, stop loading
        setLoading(false);
    }
  }, [product, fetchReviewHistory]);

  if (!product) return null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">ASIN Review History</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading review history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">ASIN Review History</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  // Helper function to parse date string (MM/DD/YY or MM/DD/YYYY) into a Date object
  const parseReviewDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      try {
          const parts = dateStr.split('/');
          if (parts.length !== 3) return null;

          const month = parseInt(parts[0], 10) - 1; // JS months are 0-indexed
          const day = parseInt(parts[1], 10);
          let year = parseInt(parts[2], 10);

          if (isNaN(month) || isNaN(day) || isNaN(year)) return null;

          // Handle 2-digit year
          if (year < 100) {
              year += 2000;
          }

          // Validate parts (simple check)
          if (month < 0 || month > 11 || day < 1 || day > 31) return null;

          return new Date(year, month, day);
      } catch {
          return null; // Return null if parsing fails
      }
  };


  // Sort entries by date (newest first)
  const sortedEntries = [...reviewEntries].sort((a, b) => {
    const dateA = parseReviewDate(a.Date) || a.date?.toDate?.() || new Date(0); // Use parsed date or fallback
    const dateB = parseReviewDate(b.Date) || b.date?.toDate?.() || new Date(0); // Use parsed date or fallback
    return dateB.getTime() - dateA.getTime(); // Descending order
  });

  // Calculate days ago from Date string
  const calculateDaysAgo = (dateStr: string): number => {
    const entryDate = parseReviewDate(dateStr);
    if (!entryDate) return 0;

    try {
      entryDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const diffTime = today.getTime() - entryDate.getTime();
      return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24))); // Ensure non-negative
    } catch (error) {
      return 0;
    }
  };

  // Format date string to "Month Day, Year" format
  const formatDate = (dateStr: string): string => {
     const date = parseReviewDate(dateStr);
     if (!date) return dateStr || 'Unknown date'; // Return original or placeholder if parsing failed

    try {
      // Format the date as "Month Day, Year"
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (error) {
      return dateStr; // Fallback to original string on formatting error
    }
  };

  // Format notes as bullet points - Simplified splitting
  const formatNotes = (note: string): string[] => {
    if (!note) return [];

    let cleanedNote = note.trim();
    // Remove "^" and all text after it
    const caretIndex = cleanedNote.indexOf('^');
    if (caretIndex !== -1) {
      cleanedNote = cleanedNote.substring(0, caretIndex).trim();
    }

    // Replace all dashes (potentially surrounded by spaces) with newlines
    // This handles "- ", " - ", and "-" used as separators between items
    const linesString = cleanedNote.replace(/\s*-\s*/g, '\n');

    // Split by newline and clean up
    return linesString.split('\n')
        .map(s => s.trim()) // Trim whitespace from each potential line
        .filter(Boolean); // Remove any empty strings resulting from multiple delimiters together
  };


  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold mb-6">ASIN Review History</h2>

      {sortedEntries.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No review history found for this ASIN.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedEntries.map((entry, index) => {
            const daysAgo = calculateDaysAgo(entry.Date);
            const notes = formatNotes(entry.note); // Use updated formatNotes
            const formattedDate = formatDate(entry.Date);

            return (
              <div key={`entry_${index}_${entry.id || Math.random().toString(36).substr(2, 9)}`} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <div className="flex flex-wrap gap-x-4 gap-y-1 items-center mb-2 text-sm">
                  <span className="font-medium">{daysAgo} days ago</span>
                  <span className="text-gray-600 dark:text-gray-400">{formattedDate}</span>
                  <span className="text-gray-600 dark:text-gray-400">{entry['Your Name'] || 'Unknown reviewer'}</span>
                </div>

                {notes.length > 0 ? (
                  <ul className="list-disc pl-5 mt-1 space-y-1 text-sm">
                    {notes.map((note, idx) => (
                      <li key={idx} className="text-gray-800 dark:text-gray-200">{note}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic mt-2 text-sm">No notes provided</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ASINReviewHistoryDashboard;